import type { FC } from 'react';
import type { ButtonProps } from '@mui/material';
import { Button, Tooltip } from '@mui/material';

export type ColorButtonProps = {
  /** color name */
  name: string;
  /** color value for css background color */
  color?: string;
  /** color (pattern) image url */
  image?: string;
  /** if color is active */
  active?: boolean;
  /** small color button */
  minimal?: boolean;
  /** rounded color button */
  rounded?: boolean;
} & Omit<ButtonProps, 'color' | 'children'>;

/**
 * ColorButton component based on https://mui.com/material-ui/react-button/
 * */
export const ColorButton: FC<ColorButtonProps> = ({
  name,
  color,
  image,
  active = false,
  minimal = true,
  rounded = true,
  disabled,
  sx,
  ...rest
}) => {
  const button = (
    <Button
      variant="text"
      className={`${rounded ? 'rounded ' : ''} ${active ? 'active' : ''}`}
      style={{
        background: `${color || 'transparent'} url(${image}) left top repeat`,
      }}
      sx={{
        display: 'block',
        width: minimal ? 30 : 36,
        height: minimal ? 30 : 36,
        minWidth: 30,
        minHeight: 'auto',
        borderRadius: 0,
        overflow: 'hidden',
        border: 2,
        borderColor: 'grey.light',
        '&:hover': {
          borderColor: 'grey.main',
        },
        '&.active': {
          outline: '2px solid black',
        },
        '&.rounded': {
          borderRadius: '50%',
        },
        ...sx,
      }}
      disabled={disabled}
      {...rest}
    />
  );

  return <Tooltip title={name}>{disabled === true ? <span>{button}</span> : button}</Tooltip>;
};
